import React, { useEffect, useState } from "react";
import {
  useChangeLanguageMutation,
  useGetAllSubCategoriesMutation,
  useGetCategoryMutation,
  useGetUserProfileMutation,
  useLogoutMutation,
} from "../../services/Post";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import {
  setAccountType,
  setChatId,
  setCreditcard,
  setHasSwitched,
  setIds,
  setProductType,
  setSubscription,
  setToken,
} from "../../app/slices/localSlice";
import { useTranslation } from "react-i18next";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import Search from "./Search";
import TrackOrder from "./TrackOrder";
import { getLanguage } from "../../javascript/SelectLanguage";
import Notification from "./Notification";

const Header = () => {
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);

  const [changeLang, { isLoading }] = useChangeLanguageMutation();
  const [logOut, { isLoading: isLoggingOut }] = useLogoutMutation();

  const handleLogout = async () => {
    Swal.fire({
      title: t("Logging Out"),
      html: t("Please wait while we log you out..."),
      allowOutsideClick: false,
      allowEscapeKey: false,
      allowEnterKey: false,
      didOpen: () => Swal.showLoading(),
      showConfirmButton: false,
    });

    try {
      const response = await logOut({ userToken });

      const errorMessage =
        response?.data?.error || response?.error?.data?.error
          ? response?.data?.message || response?.error?.data?.message
          : null;

      if (errorMessage) {
        Swal.close();
        toast.error(errorMessage);
        navigate("/login");
        return;
      }

      toast.success(response?.data?.message || t("Logged out successfully."));

      const fcmToken = localStorage.getItem("fcmTokenFalconWeb");

      localStorage.clear();
      if (fcmToken) {
        localStorage.setItem("fcmTokenFalconWeb", fcmToken);
      }

      dispatch(setToken(""));
      dispatch(setAccountType("Buyer"));
      localStorage.setItem("logoutFlag", "true");

      Swal.close();
      navigate("/login");
    } catch (err) {
      Swal.close();
      toast.error(t("An unexpected error occurred during logout."));
      console.error("Logout Error:", err);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const header = document.querySelector(".main-header");
      const body = document.querySelector("body");
      if (header) {
        if (window.scrollY > 200) {
          header.classList.add("header-fixed");
          body.classList.add("header-speacing");
        } else {
          header.classList.remove("header-fixed");
          body.classList.remove("header-speacing");
        }
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const { t, i18n } = useTranslation();

  const userToken = useSelector((state) => state?.local?.token);
  const accountType = useSelector((state) => state?.local?.accountType);

  const [category] = useGetCategoryMutation();
  const [subCategoryList] = useGetAllSubCategoriesMutation();

  const [profile] = useGetUserProfileMutation();

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [userDetails, setUserDetails] = useState("");
  const [categoryList, setCategoryList] = useState([]);
  const [subCategoryItems, setSubCategoryItems] = useState([]);
  const [allSubCategories, setAllSubCategories] = useState({});

  const location = useLocation();
  const [active, setActive] = useState(location.pathname || "/");

  useEffect(() => {
    handleSetActive(location.pathname);
  }, [location.pathname]);

  const handleSetActive = (path) => {
    setActive(path);
  };

  const handleCategory = async () => {
    const res = await category({ search: "", language: getLanguage() });
    if (res?.data?.error || res?.error?.data?.error) {
      toast.error(res?.data?.message || res?.error?.data?.message);
    } else {
      setCategoryList(res?.data?.results?.categories || []);
      prefetchSubCategories(res?.data?.results?.categories || []);
    }
  };

  const prefetchSubCategories = async (categories) => {
    try {
      const promises = categories?.map((category) =>
        subCategoryList({
          category: category?._id,
          language: getLanguage(),
          userToken,
        })
      );
      const results = await Promise.all(promises);

      const subCategories = {};
      results.forEach(({ data }, index) => {
        subCategories[categories[index]._id] =
          data?.results?.subCategories || [];
      });

      setAllSubCategories(subCategories);
      setSubCategoryItems(subCategories);
    } catch (error) {
      console.log(error);
    }
  };

  const handleOnhover = (categoryId) => {
    setSubCategoryItems(allSubCategories[categoryId] || []);
  };

  const handleProfile = async () => {
    const res = await profile({ userToken });
    if (res?.data?.error || res?.error?.data?.error) {
      toast.error(res?.data?.message || res?.error?.data?.message);
    } else {
      setUserDetails(res?.data?.results?.user);
      dispatch(setSubscription(res?.data?.results?.user?.subscription));
      dispatch(
        setCreditcard({
          creditCard: res?.data?.results?.user?.creditCard,
          fullName: res?.data?.results?.user?.fullName,
          qrCode: res?.data?.results?.user?.qrCode,
        })
      );
    }
  };

  useEffect(() => {
    handleCategory();
  }, []);
  useEffect(() => {
    if (userToken) {
      handleProfile();
    }
  }, [userToken]);

  const deviceId = localStorage.getItem("sessionId");

  const changeLanguage = async (lang) => {
    const languageMap = {
      English: "en",
      Arabic: "ar",
      Bangla: "bn",
    };
    try {
      i18n.changeLanguage(languageMap[lang] || "en");

      if (userToken) {
        const res = await changeLang({
          userToken,
          deviceId,
          language:
            languageMap[lang] === "ar"
              ? "Arabic"
              : languageMap[lang] === "bn"
              ? "Bangla"
              : "English" || "English",
        });
        if (res?.data?.error || res?.error?.data?.error) {
          toast.error(res?.data?.message || res?.error?.data?.message);
        } else {
          toast.success(res?.data?.message);
        }
      }
    } catch (error) {
      console.error("Error changing language:", error);
      toast.error("Failed to change language");
    }
  };

  const handleAuthAction = () => {
    if (userToken) {
      Swal.fire({
        title: t("Are you sure?"),
        text: t("Do you want to logout?"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: t("Yes, logout"),
        cancelButtonText: t("Cancel"),
      }).then((result) => {
        if (result.isConfirmed) {
          dispatch(setHasSwitched("true"));

          handleLogout();
          // localStorage.clear();
          // dispatch(setToken(""));
          // dispatch(setAccountType("Buyer"));
          // localStorage.setItem("logoutFlag", "true");
          // navigate("/login");
        }
      });
    } else {
      navigate("/login");
    }
  };

  const handleSwitchAccount = () => {
    const currentAccount = accountType;
    const targetAccount = currentAccount === "Buyer" ? "Seller" : "Buyer";

    Swal.fire({
      title: `${t("Switch to")} ${t(targetAccount)} ${t("Account?")}`,
      html: `
        <div style="text-align: left; margin: 10px 0;">
          <p>${t("You're about to switch from your")} <strong>${t(
        currentAccount
      )}</strong> ${t("account to")} <strong>${t(targetAccount)}</strong> ${t(
        "account."
      )}</p>
          <p>${t("This will:")}</p>
          <ul>
            <li>${t("Change your account dashboard")}</li>
            <li>${t("Provide")} ${t(targetAccount)}${t(
        "-specific features"
      )}</li>
            <li>${t("Maintain all your account data")}</li>
          </ul>
        </div>
      `,
      icon: "info",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: `${t("Switch to")} ${t(targetAccount)}`,
      cancelButtonText: t("Stay Here"),
      reverseButtons: true,
      focusConfirm: false,
      customClass: {
        popup: "account-switch-popup",
        confirmButton: "btn-confirm-switch",
        cancelButton: "btn-cancel-switch",
      },
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(setHasSwitched("true"));
        dispatch(setAccountType(targetAccount));

        Swal.fire({
          title: `${t("Switching to")} ${t(targetAccount)} ${t("Account")}`,
          html: t("Please wait while we prepare your dashboard..."),
          timerProgressBar: true,
          didOpen: () => {
            Swal.showLoading();
          },
          allowOutsideClick: false,
          allowEscapeKey: false,
          allowEnterKey: false,
        });

        setTimeout(() => {
          Swal.close();
          navigate(targetAccount === "Seller" ? "/seller-home" : "/");
        }, 800);
      }
    });
  };
  const toggleCategoryDropdown = () => {
    setIsCategoryOpen(!isCategoryOpen);
  };

  const settings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
    arrows: false,
  };

  const handleResponsiveClose = () => {
    if (window.innerWidth > 768) {
      const sidebar = document.getElementById("rightSidebar");
      if (sidebar) {
        sidebar.style.transform = "translateX(100%)";
        sidebar.style.visibility = "hidden";
        document.getElementById("closeMobileView").click();
      }
    }
  };

  useEffect(() => {
    window.addEventListener("resize", handleResponsiveClose);
    return () => window.removeEventListener("resize", handleResponsiveClose);
  }, []);

  return (
    <header className="main-header">
      <div className="top-header bg-main py-1">
        <Slider {...settings}>
          <p className="fs-14 text-center text-white fw-light m-0">
            {t("Discover over 200k products in more than 10,000 Seller")}
          </p>
          <p className="fs-14 text-center text-white fw-light m-0">
            {t("Discover over 100k Brands in more than 10,00 Seller")}
          </p>
          <p className="fs-14 text-center text-white fw-light m-0">
            {t(
              "Discover over 500k products and Category in more than 220,000 Seller"
            )}
          </p>
        </Slider>
      </div>
      <div className="container-fluid border-bottom">
        <div className="middle-header py-3">
          <div className="row align-items-center">
            <div className="col-lg-4 col-md-4 col-9 ps-0">
              <div className="d-flex align-items-center h-100">
                <div className="logo">
                  <img src="/logonew.svg" alt="" />
                </div>
              </div>
            </div>
            <Search />
            <div className="col-1 d-md-none order-2">
              <button
                className="bg-transparent"
                data-bs-toggle="offcanvas"
                data-bs-target="#rightSidebar"
              >
                <i className="fa fa-bars"></i>
              </button>
            </div>
            <div className="col-lg-4 col-md-6 col-12  order-md-1 order-2">
              <div className="d-flex justify-content-between justify-content-lg-end align-items-center">
                <div
                  className={`col-auto px-0 icon-header ${
                    i18n.language === "ar" ? "mx-3" : ""
                  }`}
                >
                  <Link
                    to="/chats"
                    // state={{ sellerId: userDetails?._id }}
                    className="bg-transparent border-0 me-2"
                    onClick={() => dispatch(setChatId(""))}
                  >
                    <img src="/assets/img/resources/message.svg" alt />
                  </Link>
                </div>
                <Notification />
                <div className="col-auto icon-header mx-2">
                  <div className="dropdown">
                    <button
                      className="bg-transparent border-0 dropdown-toggle"
                      type="button"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                    >
                      {i18n.language === "ar"
                        ? t("Arabic")
                        : i18n.language === "bn"
                        ? t("Bangla")
                        : t("English")}
                    </button>
                    <ul className="dropdown-menu">
                      {!i18n.language.startsWith("en") && (
                        <li>
                          <button
                            className="dropdown-item"
                            type="button"
                            onClick={() => changeLanguage("English")}
                          >
                            {t("English")}
                          </button>
                        </li>
                      )}
                      {i18n.language !== "ar" && (
                        <li>
                          <button
                            className="dropdown-item"
                            type="button"
                            onClick={() => changeLanguage("Arabic")}
                          >
                            {t("Arabic")}
                          </button>
                        </li>
                      )}
                      {i18n.language !== "bn" && (
                        <li>
                          <button
                            className="dropdown-item"
                            type="button"
                            onClick={() => changeLanguage("Bangla")}
                          >
                            {t("Bangla")}
                          </button>
                        </li>
                      )}
                    </ul>
                  </div>
                </div>
                <div className="col-auto ps-0">
                  <div className="dropdown profile-dropdown">
                    <button
                      className="bg-transparent border-0 dropdown-toggle d-flex gap-2"
                      type="button"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                    >
                      <div className="d-flex gap-1">
                        <div className="user">
                          {userDetails?.profileImage ? (
                            <img
                              src={userDetails.profileImage}
                              alt="User Profile"
                              className="user-image"
                            />
                          ) : (
                            <i className="fa fa-user fa-2x"></i>
                          )}
                        </div>

                        <div className="text-start">
                          <p className="m-0 fw-medium fs-14">
                            {userDetails?.fullName}
                            {accountType === "Buyer"
                              ? ""
                              : `(${t(accountType)})`}
                          </p>
                          <p
                            className="m-0 fw-light fs-14 text-light"
                            style={{ display: userToken ? "" : "none" }}
                          >
                            {userDetails?.type === "Individual"
                              ? t("Individual")
                              : t("Company")}
                          </p>
                        </div>
                      </div>
                    </button>
                    <ul className="dropdown-menu">
                      <li className="d-none">
                        <a className="dropdown-item" href="#">
                          {userDetails?.fullName}
                          <p className="fs-12 text-light m-0">
                            {userDetails?.type}
                          </p>
                        </a>
                      </li>
                      {userToken && (
                        <li>
                          <Link className="dropdown-item" to="/profile">
                            {t("My Profile")}
                          </Link>
                        </li>
                      )}

                      {/* {userToken && (
                        <li>
                          <Link
                            className="dropdown-item"
                            to="#"
                            onClick={handleSwitchAccount}
                          >
                            {accountType === "Buyer"
                              ? "Switch to Seller"
                              : "Switch to Buyer"}
                          </Link>
                        </li>
                      )} */}
                      {userToken && (
                        <li className="account-switch-item">
                          <Link
                            className="dropdown-item d-flex align-items-center"
                            to="#"
                            onClick={handleSwitchAccount}
                          >
                            <i
                              className={`bi bi-${
                                accountType === "Buyer" ? "shop" : "cart"
                              } me-2`}
                            ></i>
                            <div>
                              <div className="fw-semibold">
                                {accountType === "Buyer"
                                  ? t("Switch to Seller")
                                  : t("Switch to Buyer")}
                              </div>
                              <small className="text-muted">
                                {accountType === "Buyer"
                                  ? t("Access seller dashboard")
                                  : t("Return to buyer view")}
                              </small>
                            </div>
                          </Link>
                        </li>
                      )}
                      <li>
                        <button
                          className="dropdown-item"
                          onClick={handleAuthAction}
                        >
                          {userToken ? t("Logout") : t("Login")}
                        </button>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="container-fluid  d-md-block d-none">
        <div className="bottom-header border-bottom">
          <ul className="navbar list-unstyled justify-content-start">
            <li className="nav-item pe-4">
              <Link
                // className={`nav-link ${active === "/" ? "active" : ""}`}
                className={`nav-link ${
                  active === "/" || active === "/seller-home" ? "active" : ""
                }`}
                to={accountType === "Buyer" ? "/" : "/seller-home"}
                onClick={() => handleSetActive("/")}
              >
                {t("Home")}
              </Link>
            </li>
            <li className="nav-item pe-4">
              <Link
                className={`nav-link ${
                  active === "/product-filter" || active === "/seller-product"
                    ? "active"
                    : ""
                }`}
                to={
                  accountType === "Buyer"
                    ? "/product-filter"
                    : "/seller-product"
                }
                onClick={() => {
                  handleSetActive("/product-filter");
                  localStorage.removeItem("productType");
                  dispatch(setProductType(""));
                  dispatch(setIds(""));
                }}
              >
                {accountType === "Buyer" ? t("Products") : t("My Products")}
              </Link>
            </li>

            <li className="nav-item pe-4 dropdown category-dropdown">
              <a className="nav-link" role="button" data-bs-toggle="dropdown">
                {t("Categories")}
              </a>
              <ul className="dropdown-menu">
                {categoryList?.length > 0
                  ? categoryList?.map((ite, ind) => {
                      return (
                        <li className="dropdown-submenu" key={ind}>
                          <a
                            href="#"
                            className="dropdown-item"
                            onMouseEnter={() => handleOnhover(ite?._id)}
                          >
                            {i18n.language === "ar"
                              ? ite?.name_ar
                              : i18n.language === "bn"
                              ? ite?.name_bn
                              : ite?.name_en}
                          </a>
                          <ul className="dropdown-menu">
                            {subCategoryItems?.length > 0 ? (
                              subCategoryItems?.map((sub, subInd) => {
                                return (
                                  <li className="m-2" key={subInd}>
                                    <Link
                                      className="dropdown-item"
                                      to="/product-filter"
                                      onClick={() => {
                                        dispatch(setIds(sub?._id));
                                        dispatch(setProductType("subCategory"));
                                      }}
                                      state={{ id: sub?._id }}
                                    >
                                      {i18n.language === "ar"
                                        ? sub?.name_ar
                                        : i18n.language === "bn"
                                        ? sub?.name_bn
                                        : sub?.name_en}
                                    </Link>
                                  </li>
                                );
                              })
                            ) : (
                              <li
                                className="m-2 text-danger"
                                style={{ width: "max-content" }}
                              >
                                {t("No subcategories available")}
                              </li>
                            )}
                          </ul>
                        </li>
                      );
                    })
                  : "No categories available"}
              </ul>
            </li>
            {accountType === "Buyer" ? null : (
              <li className="nav-item pe-4">
                <Link
                  className={`nav-link ${
                    active === "/my-earning" ? "active" : ""
                  }`}
                  to="/my-earning"
                  onClick={() => handleSetActive("/my-earning")}
                >
                  {t("My Earning")}
                </Link>
              </li>
            )}

            <li className="nav-item pe-4">
              <Link
                className={`nav-link ${
                  active === "/tender-news" ? "active" : ""
                }`}
                to="/tender-news"
                onClick={() => handleSetActive("/tender-news")}
              >
                {t("Tender News")}
              </Link>
            </li>
            <li className="nav-item pe-4">
              <Link
                className={`nav-link ${active === "/faqs" ? "active" : ""}`}
                to="/faqs"
                onClick={() => handleSetActive("/faqs")}
              >
                {t("FAQs")}
              </Link>
            </li>

            <TrackOrder />
          </ul>
        </div>
      </div>

      {/* ✅ Right Sidebar (Mobile Header Menu in mobile mode) */}
      <div className="offcanvas offcanvas-end" tabIndex="-1" id="rightSidebar">
        <div className="offcanvas-header">
          <div className="logo">
            <img src="/assets/img/comman/logo-text-blue.png" alt="Logo" />
          </div>
          <button
            type="button"
            className="btn-close text-white"
            data-bs-dismiss="offcanvas"
            id="closeMobileView"
          ></button>
        </div>
        <div className="offcanvas-body">
          <ul className="navbar list-unstyled align-items-start flex-column">
            <li className="nav-item mb-2 pe-4">
              <Link
                className="nav-link active"
                to={accountType === "Buyer" ? "/" : "seller-home"}
                onClick={() => {
                  dispatch(setIds(""));
                  document.getElementById("closeMobileView").click();
                }}
              >
                {t("Home")}
              </Link>
            </li>
            <li className="nav-item mb-2 pe-4">
              <Link
                className={`nav-link ${
                  active === "/product-filter" || active === "/seller-product"
                    ? "active"
                    : ""
                }`}
                to={
                  accountType === "Buyer"
                    ? "/product-filter"
                    : "/seller-product"
                }
                onClick={() => {
                  handleSetActive("/product-filter");
                  localStorage.removeItem("productType");
                  dispatch(setProductType(""));
                  dispatch(setIds(""));
                  document.getElementById("closeMobileView").click();
                }}
              >
                {accountType === "Buyer" ? t("Products") : t("My Products")}
              </Link>
            </li>

            <li className="nav-item mb-2 pe-4 w-100">
              <div className="accordion w-100 p-0" id="categoryAccordion">
                <div className="accordion-item border-0 py-0 w-100">
                  <h2 className="accordion-header">
                    <button
                      className="accordion-button collapsed bg-transparent shadow-none px-0 text-start w-100 p-0"
                      type="button"
                      data-bs-toggle="collapse"
                      data-bs-target="#categoryCollapse"
                      aria-expanded="false"
                      aria-controls="categoryCollapse"
                    >
                      {t("Categories")}
                    </button>
                  </h2>
                  <div
                    id="categoryCollapse"
                    className="accordion-collapse collapse bg-main-light"
                    data-bs-parent="#categoryAccordion"
                  >
                    <div className="accordion-body">
                      <ul className="list-unstyled">
                        {categoryList?.length > 0 ? (
                          categoryList.map((ite, ind) => (
                            <li key={ind} className="dropdown-item mb-2">
                              <h2 className="accordion-header">
                                <button
                                  className="accordion-button collapsed bg-transparent shadow-none px-0 text-start w-100 p-0"
                                  type="button"
                                  data-bs-toggle="collapse"
                                  aria-expanded="false"
                                  data-bs-target={`#subCategoryCollapse${ind}`}
                                  aria-controls={`subCategoryCollapse${ind}`}
                                  onMouseEnter={() => handleOnhover(ite?._id)}
                                  onClick={() => dispatch(setIds(""))}
                                >
                                  {i18n.language === "ar"
                                    ? ite?.name_ar
                                    : i18n.language === "bn"
                                    ? ite?.name_bn
                                    : ite?.name_en}
                                </button>
                              </h2>
                              <div
                                id={`subCategoryCollapse${ind}`}
                                className="accordion-collapse collapse"
                                data-bs-parent="#categoryCollapse"
                              >
                                <div className="accordion-body">
                                  <ul className="list-unstyled">
                                    {subCategoryItems?.length > 0 ? (
                                      subCategoryItems.map((sub, subInd) => (
                                        <li key={subInd} className="m-2">
                                          <Link
                                            className="dropdown-item"
                                            to="/sub-category-view"
                                            onClick={() => {
                                              dispatch(setIds(sub?._id));
                                              dispatch(
                                                setProductType("subCategory")
                                              );
                                              document
                                                .getElementById(
                                                  "closeMobileView"
                                                )
                                                .click();
                                            }}
                                            state={{ id: sub?._id }}
                                          >
                                            {i18n.language === "ar"
                                              ? sub?.name_ar
                                              : i18n.language === "bn"
                                              ? sub?.name_bn
                                              : sub?.name_en}
                                          </Link>
                                        </li>
                                      ))
                                    ) : (
                                      <li
                                        className="m-2 text-danger"
                                        style={{ width: "max-content" }}
                                      >
                                        {t("No subcategories available")}
                                      </li>
                                    )}
                                  </ul>
                                </div>
                              </div>
                            </li>
                          ))
                        ) : (
                          <li className="m-2 text-danger">
                            {t("No categories available")}
                          </li>
                        )}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </li>

            <li className="nav-item mb-2 pe-4">
              <Link
                className="nav-link"
                to="/tender-news"
                onClick={() => {
                  dispatch(setIds(""));
                  document.getElementById("closeMobileView").click();
                }}
              >
                {t("Tender News")}
              </Link>
            </li>
            <li className="nav-item mb-2 pe-4">
              <Link
                className="nav-link"
                to="/faqs"
                onClick={() => {
                  dispatch(setIds(""));
                  document.getElementById("closeMobileView").click();
                }}
              >
                {t("FAQs")}
              </Link>
            </li>
            <div className="dropdown">
              <button
                className="nav-link btn btn-outline-primary rounded-pill px-3 py-1"
                type="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <i className="fa fa-truck me-2"></i> {t("Track Your Order")}
              </button>
              <div
                className="dropdown-menu p-3 shadow-lg rounded-3 track-your-order"
                style={{ minWidth: "300px" }}
              >
                <h6 className="fw-semibold mb-2">{t("Track Order")}</h6>

                {/* Order ID input */}
                <div className="input-group mb-3">
                  <input
                    type="text"
                    className="form-control"
                    placeholder={t("Enter Product / Order ID")}
                  />
                  <button className="btn btn-primary">{t("Track")}</button>
                </div>
              </div>
            </div>
          </ul>
        </div>
      </div>
    </header>
  );
};

export default Header;
